import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate
} from 'react-router-dom';
import { BillingNitif } from './components/BillingNotif/BillingNotif';
import Header from './components/Header/Header';
import Sidebar from './components/Sidebar/Sidebar';
import api from './config/api';
import apiv2 from './config/apiv2';
import { PERMISSIONS } from './contants/permissions';
import { setDataSource, setIsOpenNewDataSourceModal } from './features/dataSource/dataSourceSlice';
import { setMe } from './features/me/meSlice';
import CreateAccount from './pages/CreateAccount/CreateAccount';
import CreateBudget from './pages/CreateBudget/CreateBudget';
import CreateClient from './pages/CreateClient/CreateClient';
import Dashboard from './pages/Dashboard/Dashboard';
import DataSources from './pages/DataSources/DataSources';
import LoginPage from './pages/LoginPage/LoginPage';
import ConfirmInvitationPage from './pages/LoginPage/СonfirmInvitationPage';
import ResetPassword from './pages/ResetPassword/ResetPassword';
// import SignupPageV2 from './pages/SignupPage';
import SignupPage from './pages/SignupPage/SignupPage';
import SignupPageV2 from './pages/SignupPageV2';
import Team from './pages/Team/Team';
import AutomatedRules from './pages/UserSettings/AutomatedRules';
import UserSettings from './pages/UserSettings/UserSettings';
import css from './styles.module.css';

// const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);
const sources = ['google'];

const ProtectedRoute = ({ children, allowedPermissions }) => {
  //change trigger to !localStorage.getItem('ACCESS_TOKEN') or !cookies
  const [isAuthenticated, setAuthenticated] = useState(false);
  
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const checkAccess = async () => {
    try {
      const { data: currUser } = await apiv2.GET_ME();
      dispatch(setMe(currUser));
      setAuthenticated(true);

      const permissionOfCurrUser = currUser?.permission_level?.name;

      if (!allowedPermissions?.includes(permissionOfCurrUser)) {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (allowedPermissions.length) {
      checkAccess();
    }
  }, []);

  if (!localStorage.getItem('ACCESS_TOKEN')) {
    localStorage.removeItem('REFRESH_TOKEN');
    return <Navigate to="/login" replace />;
  }

  return (
    isAuthenticated && <main className={`${css.mainWrapper}`}>{children}</main>
  );
};

const routeWithoutHeader = [
  '/signup',
  '/signupv2',
  '/login',
  '/reset-password',
  '/confirm-invitation/',
  '/create-account'
];

const App = () => {
  const location = useLocation();
  const dispatch = useDispatch();

  const isPossibleRoute = location.pathname && !routeWithoutHeader.includes(location.pathname)
  const getIsLoadMain = !!useSelector((state) => state.me?.id);
  const isLoadMainAndPossibleRoute = isPossibleRoute && getIsLoadMain
  const isNeedShowNotifDataSource = !useSelector((state) => state.me?.has_data_sources) && isLoadMainAndPossibleRoute;
  const isNeedShowNotifClient = !useSelector((state) => state.me?.has_clients) && isLoadMainAndPossibleRoute;
  const isNeedShowNotifBudget = !useSelector((state) => state.me?.has_budgets) && isLoadMainAndPossibleRoute;

  const [showNotif, setShowNotif] = useState(false);
  const [showNotifAccount, setShowNotifAccount] = useState(false);
  const [billingNotification, setBillingNotification] = useState({
    boldText: '',
    text: '',
  });
  const [accountNotification, setAccountNotification] = useState({
    boldText: '',
    text: '',
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (!location) return;
    if (location.pathname === '/login') {
      setShowNotif(false);
      setBillingNotification({
        boldText: '',
        text: '',
      });
    }
  }, [location]);

  function linkCreator (){
    
    if(isNeedShowNotifDataSource){
      navigate('/data-sources')
      dispatch(setIsOpenNewDataSourceModal(true));
      return;
    }else if(isNeedShowNotifClient && !isNeedShowNotifDataSource){
      navigate('/create-client')
      return;
    }else if (isNeedShowNotifBudget && !isNeedShowNotifClient){
      navigate('/create-budget/client')
      return;
    } else{
      return;
    }
  }

  useEffect(() => {
    const isNeedNotif = isNeedShowNotifDataSource || isNeedShowNotifClient || isNeedShowNotifBudget
    if(isLoadMainAndPossibleRoute && !showNotif && isNeedNotif){
      setShowNotifAccount(true)
      if(isNeedShowNotifDataSource){
        setAccountNotification({
          boldText: '',
          text: 'To get started click here to add a data source',
        });
      }else if(isNeedShowNotifClient && !isNeedShowNotifDataSource){
        setAccountNotification({
          boldText: 'Step 2: ',
          text: 'click here to create a Client',
        });
      }else if (isNeedShowNotifBudget && !isNeedShowNotifClient){
        setAccountNotification({
          boldText: 'Final Step: ',
          text: 'click here to create a Budget',
        });
      } else{
        return
      }
    }else{
      setShowNotifAccount(false)
      setAccountNotification({
        boldText: '',
        text: '',
      });
    }
  }, [
    isNeedShowNotifDataSource,
    isNeedShowNotifClient,
    isNeedShowNotifBudget,
    isLoadMainAndPossibleRoute,
    getIsLoadMain,
    location
  ]);

  useEffect(() => {
    (async () => {
      if (location.pathname.includes('confirm-invitation')) return;
      if (localStorage.getItem('ACCESS_TOKEN')) {
        apiv2.GET_ME().then(res => {
          dispatch(setMe(res.data));
          if (res.data.permission_level.name === 'Admin') {
            if (res.data.notifications.trial_period_expired) {
              setShowNotif(true);
              navigate('/settings/usage-billing');
              setBillingNotification({
                boldText: 'Free trial has expired.',
                text: 'Click here to add a payment method',
              });
            }
            if (res.data.notifications.trial_period_remaining_warning) {
              setShowNotif(true);
              setBillingNotification({
                boldText: `Free trial expires in ${res.data.notifications.trial_remaining_days} days.`,
                text: 'Click here to add a payment method',
              });
            }
            if (res.data.notifications.payment_error) {
              setShowNotif(true);
              navigate('/settings/usage-billing');
              setBillingNotification({
                boldText: `Make a payment towards your overdue balance in order to gain access to your dashboard again.`,
              });
            }
          }

          if (
            res.data.permission_level.name === 'Standard' ||
            res.data.permission_level.name === 'Read Only'
          ) {
            if (res.data.notifications.trial_period_expired) {
              setShowNotif(true);
              navigate('/settings/usage-billing');
              setBillingNotification({
                boldText: 'Free trial has expired.',
                text: 'Contact your account to admin regain access to your dashboard',
              });
            }
            if (res.data.notifications.payment_error) {
              setShowNotif(true);
              setBillingNotification({
                boldText: 'Overdue balance.',
                text: 'Contact your account to admin regain access to your dashboard',
              });
            }
          }

          if (res.data.notifications.payment_failed_warning) {
            setShowNotif(true);
            setBillingNotification({
              boldText: `Make a payment towards your overdue balance.`,
              text: `You will not be able to view your dashboard in ${res.data.notifications.payment_remaining_days} days if this isn't resolved.`,
            });
          }
        });

        Promise.all(
          sources.map(source => {
            if (source === 'google') {
              return api.GET_GOOGLE_DATA_SOURCES().then(res => {
                return res;
              });
            }
          }),
        ).then(res => {
          const concated = res.reduce(
            (previousValue, currentValue) => previousValue.concat(currentValue),
            [],
          );

          dispatch(setDataSource(concated));
        });
      }
    })();
  }, []);

  return (
    <>
      {/* <Elements stripe={stripePromise}> */}
        {showNotif && (
          <BillingNitif
            boldText={billingNotification.boldText}
            text={billingNotification.text}
            onClick={() => {
              billingNotification.text ===
                'Click here to add a payment method' &&
                navigate('/settings/usage-billing');
            }}
          />
        )}
        {showNotifAccount && (
          <BillingNitif
            boldText={accountNotification.boldText}
            text={accountNotification.text}
            onClick={linkCreator}
          />
        )}
        {location.pathname !== '/signup' && location.pathname !== '/signupv2' &&
          location.pathname !== '/create-account' &&
          location.pathname !== '/login' &&
          !location.pathname.includes('/reset-password') &&
          !location.pathname.includes('/confirm-invitation/') && <Header />}
        {location.pathname !== '/signup' && location.pathname !== '/signupv2' &&
          location.pathname !== '/create-account' &&
          location.pathname !== '/login' &&
          !location.pathname.includes('/reset-password') &&
          !location.pathname.includes('/confirm-invitation/') && <Sidebar />}
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          {/* <Route path="/signup" element={<SignupPage />} /> */}
          <Route path="/signup" element={<SignupPageV2 />} />
          <Route path="/create-account" element={<CreateAccount />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/reset-password/:reset_token"
            element={<ResetPassword />}
          />
          <Route
            path="/confirm-invitation/:token"
            element={<ConfirmInvitationPage />}
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute
                allowedPermissions={[
                  PERMISSIONS.ADMIN,
                  PERMISSIONS.STANDARD,
                  PERMISSIONS.READ_ONLY,
                ]}
              >
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/create-client"
            element={
              <ProtectedRoute
                allowedPermissions={[PERMISSIONS.ADMIN, PERMISSIONS.STANDARD]}
              >
                <CreateClient />
              </ProtectedRoute>
            }
          />
          <Route
            path="/create-client/:id"
            element={
              <ProtectedRoute
                allowedPermissions={[PERMISSIONS.ADMIN, PERMISSIONS.STANDARD]}
              >
                <CreateClient />
              </ProtectedRoute>
            }
          />
          <Route
            path="/create-budget/:step"
            element={
              <ProtectedRoute
                allowedPermissions={[PERMISSIONS.ADMIN, PERMISSIONS.STANDARD]}
              >
                <CreateBudget />
              </ProtectedRoute>
            }
          />
          <Route
            path="/create-budget/:step/:id"
            element={
              <ProtectedRoute
                allowedPermissions={[PERMISSIONS.ADMIN, PERMISSIONS.STANDARD]}
              >
                <CreateBudget />
              </ProtectedRoute>
            }
          />
          <Route
            path="/team"
            element={
              <ProtectedRoute
                allowedPermissions={[PERMISSIONS.ADMIN, PERMISSIONS.STANDARD]}
              >
                <Team />
              </ProtectedRoute>
            }
          />
          <Route
            path="/data-sources"
            element={
              <ProtectedRoute
                allowedPermissions={[PERMISSIONS.ADMIN, PERMISSIONS.STANDARD]}
              >
                <DataSources />
              </ProtectedRoute>
            }
          />
          <Route
            path="/data-sources/:source"
            element={
              <ProtectedRoute
                allowedPermissions={[PERMISSIONS.ADMIN, PERMISSIONS.STANDARD]}
              >
                <DataSources />
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings/automated-rules"
            element={
              <ProtectedRoute
                allowedPermissions={[PERMISSIONS.ADMIN, PERMISSIONS.STANDARD]}
              >
                <AutomatedRules />
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings/automated-rules/:id"
            element={
              <ProtectedRoute
                allowedPermissions={[PERMISSIONS.ADMIN, PERMISSIONS.STANDARD]}
              >
                <AutomatedRules />
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings/user"
            element={
              <ProtectedRoute
                allowedPermissions={[
                  PERMISSIONS.ADMIN,
                  PERMISSIONS.STANDARD,
                  PERMISSIONS.READ_ONLY,
                ]}
              >
                <UserSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/account"
            element={
              <ProtectedRoute allowedPermissions={[PERMISSIONS.ADMIN]}>
                <UserSettings />
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings/usage-billing"
            element={
              <ProtectedRoute allowedPermissions={[PERMISSIONS.ADMIN]}>
                <UserSettings />
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<Navigate replace to="/dashboard" />} />
          <Route path="/" element={<Navigate replace to="/dashboard" />} />
        </Routes>
      {/* </Elements> */}
    </>
  );
};

export default App;
